import React, { useEffect } from 'react';
import Button from '../../BaseComponents/Button';
import { useFormikContext } from 'formik';
import TextInput from '../../BaseComponents/TextInput';
import Select from '../../BaseComponents/Select';
import MultiSelect from '../../BaseComponents/MultiSelect';
import {
  activityLevelOptions,
  alcoholFrequencyOptions,
  calmingActivityOptions,
  dietTypeOptions,
  eatingPatternOptions,
  exerciseRoutineOptions,
  hydrationOptions,
  OnboardingFormValues,
  overallHealthOptions,
  shouldShowFemaleSexHormonesStep,
  smokingStatusOptions,
  workingStyleOptions
} from '../../../types/membersTypes';
import { StepProps } from '../../../pages/complete-profile';
import './FormQuestion.scss';

type Step2Props = {
  setCurrentStep: (step: StepProps) => void;
  saveStepData?: (stepNumber: number) => void;
  populateFormWithSavedData?: () => void;
  lastCompletedStep?: number;
};

const Step2: React.FC<Step2Props> = ({
  setCurrentStep,
  saveStepData,
  populateFormWithSavedData
}) => {
  const { values, errors, setFieldValue, setFieldError } =
    useFormikContext<OnboardingFormValues>();

  const [hasPopulated, setHasPopulated] = React.useState(false);

  useEffect(() => {
    if (populateFormWithSavedData && !hasPopulated) {
      populateFormWithSavedData();
      setHasPopulated(true);
    }
  }, [populateFormWithSavedData, hasPopulated]);

  const handleDietTypesChange = (option: { key: any; value: any }, name: string) => {
    const currentOptions = values.diet_types || [];
    const isSelected = currentOptions.some((opt) => opt.key === option.key);
    const updatedOptions = isSelected
      ? currentOptions.filter((opt) => opt.key !== option.key)
      : [...currentOptions, option];

    setFieldValue(name, updatedOptions);
    setFieldError(name, undefined);

    if (!updatedOptions.some((opt) => opt.key === 'Other')) {
      setFieldValue('other_diet_type', '');
      setFieldError('other_diet_type', undefined);
    }
  };

  const handleCalmingActivitiesChange = (option: { key: any; value: any }, name: string) => {
    const currentOptions = values.calming_activities || [];
    const isSelected = currentOptions.some((opt) => opt.key === option.key);
    const updatedOptions = isSelected
      ? currentOptions.filter((opt) => opt.key !== option.key)
      : [...currentOptions, option];

    setFieldValue(name, updatedOptions);
    setFieldError(name, undefined);

    if (!updatedOptions.some((opt) => opt.key === 'Other')) {
      setFieldValue('other_calming_activity', '');
      setFieldError('other_calming_activity', undefined);
    }
  };

  const validateStepFields = () => {
    const validationErrors: Record<string, string> = {};

    const requireField = (field: string, condition: boolean, message: string) => {
      if (!condition) {
        validationErrors[field] = message;
        setFieldError(field, message);
      }
    };

    const workingStyleIsOther = values.working_style?.key === 'Other';
    const dietTypesIncludeOther = values.diet_types?.some((d: any) => d.key === 'Other');
    const eatingPatternIsOther = values.eating_pattern?.key === 'Other';
    const hydrationIsOther = values.hydration?.key === 'Other';
    const exerciseRoutineIsOther = values.exercise_routine?.key === 'Other';
    const calmingActivitiesIncludeOther = values.calming_activities?.some((a: any) => a.key === 'Other');

    requireField('overall_health', !!values.overall_health?.value, 'Overall health is required');
    requireField('activity_level', !!values.activity_level?.value, 'Activity level is required');
    requireField('exercise_routine', !!values.exercise_routine?.value, 'Exercise routine is required');
    if (exerciseRoutineIsOther) {
      requireField(
        'other_exercise_routine',
        !!values.other_exercise_routine?.trim(),
        'Please specify your exercise routine'
      );
    }

    requireField('smoking_status', !!values.smoking_status?.value, 'Smoking status is required');
    requireField('alcohol_frequency', !!values.alcohol_frequency?.value, 'Alcohol frequency is required');
    requireField('working_style', !!values.working_style?.value, 'Working style is required');

    if (workingStyleIsOther) {
      requireField(
        'other_working_style',
        !!values.other_working_style?.trim(),
        'Please specify your working style'
      );
    }

    requireField('diet_types', !!values.diet_types?.length, 'Please select at least one diet type');

    if (dietTypesIncludeOther) {
      requireField('other_diet_type', !!values.other_diet_type?.trim(), 'Please specify your diet type');
    }

    requireField('eating_pattern', !!values.eating_pattern?.value, 'Eating pattern is required');
    if (eatingPatternIsOther) {
      requireField(
        'other_eating_pattern',
        !!values.other_eating_pattern?.trim(),
        'Please specify your eating pattern'
      );
    }

    requireField('hydration', !!values.hydration?.value, 'Hydration method is required');
    if (hydrationIsOther) {
      requireField(
        'other_hydration',
        !!values.other_hydration?.trim(),
        'Please specify your hydration method'
      );
    }

    requireField('calming_activities', !!values.calming_activities?.length, 'Please select at least one activity');
    if (calmingActivitiesIncludeOther) {
      requireField(
        'other_calming_activity',
        !!values.other_calming_activity?.trim(),
        'Please specify your calming activity'
      );
    }

    return validationErrors;
  };

  const goToTheNextStep = async () => {
    const validationErrors = validateStepFields();

    if (Object.keys(validationErrors).length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      if (saveStepData) {
        await saveStepData(2);
      }
    } catch {
      return;
    }

    const isFemale = values.gender?.key === 'female';
    if (shouldShowFemaleSexHormonesStep(isFemale)) {
      setCurrentStep({ number: 3, name: 'Required: Female Sex Hormones' });
    } else {
      setCurrentStep({ number: 3, name: 'Medical History' });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className='step-2'>
      <div className='select-arrow-pos1'>
        <Select
          title='How would you describe your overall health? *'
          name='overall_health'
          placeholder='Select your overall health'
          options={overallHealthOptions}
          selectedOption={values.overall_health}
          onChange={(option) => {
            setFieldValue('overall_health', option);
            setFieldError('overall_health', undefined);
          }}
          isError={!!errors.overall_health}
          errorMessage={errors.overall_health as string}
        />
      </div>
      <div className='select-arrow-pos1'>
        <Select
          title='How would you describe your activity level? *'
          name='activity_level'
          placeholder='Select your activity level'
          options={activityLevelOptions}
          selectedOption={values.activity_level}
          onChange={(option) => {
            setFieldValue('activity_level', option);
            setFieldError('activity_level', undefined);
          }}
          isError={!!errors.activity_level}
          errorMessage={errors.activity_level as string}
        />
      </div>
      <div className='select-arrow-pos1'>
        <Select
          title='How would you describe your exercise routine? *'
          name='exercise_routine'
          placeholder='Select your exercise routine'
          options={exerciseRoutineOptions}
          selectedOption={values.exercise_routine}
          onChange={(option) => {
            setFieldValue('exercise_routine', option);
            setFieldError('exercise_routine', undefined);

            if (option.key !== 'Other') {
              setFieldValue('other_exercise_routine', '');
              setFieldError('other_exercise_routine', undefined);
            }
          }}
          isError={!!errors.exercise_routine}
          errorMessage={errors.exercise_routine as string}
        />
      </div>
      {values.exercise_routine?.key === 'Other' && (
        <TextInput
          title='Please specify your exercise routine *'
          name='other_exercise_routine'
          placeholder='Enter your exercise routine'
          isError={!!errors.other_exercise_routine}
          errorMessage={errors.other_exercise_routine as string}
          onChange={(e) => {
            setFieldValue('other_exercise_routine', e.target.value);
            setFieldError('other_exercise_routine', undefined);
          }}
          value={values.other_exercise_routine}
        />
      )}
      <div className='select-arrow-pos1'>
        <Select
          title='Do you smoke or use nicotine products? *'
          name='smoking_status'
          placeholder='Select your smoking status'
          options={smokingStatusOptions}
          selectedOption={values.smoking_status}
          onChange={(option) => {
            setFieldValue('smoking_status', option);
            setFieldError('smoking_status', undefined);
          }}
          isError={!!errors.smoking_status}
          errorMessage={errors.smoking_status as string}
        />
      </div>
      <div className='select-arrow-pos1'>
        <Select
          title='How often do you drink alcohol? *'
          name='alcohol_frequency'
          placeholder='Select your alcohol frequency'
          options={alcoholFrequencyOptions}
          selectedOption={values.alcohol_frequency}
          onChange={(option) => {
            setFieldValue('alcohol_frequency', option);
            setFieldError('alcohol_frequency', undefined);
          }}
          isError={!!errors.alcohol_frequency}
          errorMessage={(errors.alcohol_frequency as string) || undefined}
        />
      </div>
      <div className='select-arrow-pos1'>
        <Select
          title='What best describes your working style? *'
          name='working_style'
          placeholder='Select your working style'
          options={workingStyleOptions}
          selectedOption={values.working_style}
          onChange={(option) => {
            setFieldValue('working_style', option);
            setFieldError('working_style', undefined);

            if (option.key !== 'Other') {
              setFieldValue('other_working_style', '');
              setFieldError('other_working_style', undefined);
            }
          }}
          isError={!!errors.working_style}
          errorMessage={errors.working_style as string}
        />
      </div>
      {values.working_style?.key === 'Other' && (
        <TextInput
          title='Please specify your working style *'
          name='other_working_style'
          placeholder='Enter your working style'
          isError={!!errors.other_working_style}
          errorMessage={errors.other_working_style as string}
          onChange={(e) => {
            setFieldValue('other_working_style', e.target.value);
            setFieldError('other_working_style', undefined);
          }}
          value={values.other_working_style}
        />
      )}
      <div className='input-wrapper'>
        <MultiSelect
          title='Do you follow a particular diet? *'
          name='diet_types'
          placeholder='Select your diet types'
          options={dietTypeOptions}
          selectedOptions={values.diet_types}
          onChange={handleDietTypesChange}
          isError={!!errors.diet_types}
        />
        {errors.diet_types && (
          <div className='error-message'>{errors.diet_types as unknown as string}</div>
        )}
      </div>
      {values.diet_types?.some((diet) => diet.key === 'Other') && (
        <TextInput
          title='Please specify your diet type *'
          name='other_diet_type'
          placeholder='Enter your diet type'
          isError={!!errors.other_diet_type}
          errorMessage={errors.other_diet_type as string}
          onChange={(e) => {
            setFieldValue('other_diet_type', e.target.value);
            setFieldError('other_diet_type', undefined);
          }}
          value={values.other_diet_type}
        />
      )}
      <div className='select-arrow-pos1'>
        <Select
          title='How would you describe your typical eating pattern? *'
          name='eating_pattern'
          placeholder='Select your eating pattern'
          options={eatingPatternOptions}
          selectedOption={values.eating_pattern}
          onChange={(option) => {
            setFieldValue('eating_pattern', option);
            setFieldError('eating_pattern', undefined);

            if (option.key !== 'Other') {
              setFieldValue('other_eating_pattern', '');
              setFieldError('other_eating_pattern', undefined);
            }
          }}
          isError={!!errors.eating_pattern}
          errorMessage={errors.eating_pattern as string}
        />
      </div>
      {values.eating_pattern?.key === 'Other' && (
        <TextInput
          title='Please specify your eating pattern *'
          name='other_eating_pattern'
          placeholder='Enter your eating pattern'
          isError={!!errors.other_eating_pattern}
          errorMessage={errors.other_eating_pattern as string}
          onChange={(e) => {
            setFieldValue('other_eating_pattern', e.target.value);
            setFieldError('other_eating_pattern', undefined);
          }}
          value={values.other_eating_pattern}
        />
      )}
      <div className='select-arrow-pos1'>
        <Select
          title='How do you usually hydrate? *'
          name='hydration'
          placeholder='Select your hydration method'
          options={hydrationOptions}
          selectedOption={values.hydration}
          onChange={(option) => {
            setFieldValue('hydration', option);
            setFieldError('hydration', undefined);

            if (option.key !== 'Other') {
              setFieldValue('other_hydration', '');
              setFieldError('other_hydration', undefined);
            }
          }}
          isError={!!errors.hydration}
          errorMessage={errors.hydration as string}
        />
      </div>
      {values.hydration?.key === 'Other' && (
        <TextInput
          title='Please specify your hydration method *'
          name='other_hydration'
          placeholder='Enter your hydration method'
          isError={!!errors.other_hydration}
          errorMessage={errors.other_hydration as string}
          onChange={(e) => {
            setFieldValue('other_hydration', e.target.value);
            setFieldError('other_hydration', undefined);
          }}
          value={values.other_hydration}
        />
      )}
      <div className='input-wrapper'>
        <MultiSelect
          title='Do you practise any calming or restorative activities? *'
          name='calming_activities'
          placeholder='Select your calming activities'
          options={calmingActivityOptions}
          selectedOptions={values.calming_activities}
          onChange={handleCalmingActivitiesChange}
          isError={!!errors.calming_activities}
        />
        {errors.calming_activities && (
          <div className='error-message'>{errors.calming_activities as unknown as string}</div>
        )}
      </div>
      {values.calming_activities?.some((activity) => activity.key === 'Other') && (
        <TextInput
          title='Please specify your calming activity *'
          name='other_calming_activity'
          placeholder='Enter your calming activity'
          isError={!!errors.other_calming_activity}
          errorMessage={errors.other_calming_activity as string}
          onChange={(e) => {
            setFieldValue('other_calming_activity', e.target.value);
            setFieldError('other_calming_activity', undefined);
          }}
          value={values.other_calming_activity}
        />
      )}

      <div className='submit-btn-wrapper'>
        <Button
          text='Back'
          onClick={() => setCurrentStep({ number: 1, name: 'Basics' })}
          backgroundColor={'#ffffff'}
          color={'#2D334E'}
        />
        <Button text='Next' onClick={goToTheNextStep} />
      </div>
    </div>
  );
};
export default Step2;
